import {
  U21Modal,
  U21Form,
  U21FormField,
  U21FormFieldTypes,
  U21Spacer,
  U21Typography,
  U21Chip,
} from 'app/shared/u21-ui/components';

import { RiskModelSummary } from 'app/modules/riskRatings/responses';
import { useClearRiskScoreOverride } from 'app/modules/riskRatings/queries/useClearRiskScoreOverride';
import { getRiskCategoryStyles } from 'app/modules/riskRatings/helpers';
import { IconAlertTriangle } from '@u21/tabler-icons';

interface Props {
  open: boolean;
  onClose: () => void;
  entityExternalId: string;
  modelScore: number;
  riskModelMetadata: Pick<
    RiskModelSummary,
    'high_risk_floor' | 'low_risk_ceiling'
  >;
}

interface ClearOverrideFormValues {
  comment: string;
}

const FORM_ID = 'clear-override-form';

export const ClearOverrideConfirmModal = ({
  open,
  onClose,
  entityExternalId,
  modelScore,
  riskModelMetadata,
}: Props) => {
  const { mutateAsync, isPending } =
    useClearRiskScoreOverride(entityExternalId);

  const riskStyles = getRiskCategoryStyles(
    modelScore,
    riskModelMetadata.high_risk_floor,
    riskModelMetadata.low_risk_ceiling,
  );

  return (
    <U21Modal
      open={open}
      onClose={onClose}
      title="Clear Override"
      actionButtonProps={{
        children: 'Clear Override',
        color: 'error',
        form: FORM_ID,
        type: 'submit',
        loading: isPending,
      }}
    >
      <U21Form<ClearOverrideFormValues>
        id={FORM_ID}
        onSubmit={async (values: ClearOverrideFormValues) => {
          try {
            await mutateAsync({ comment: values.comment.trim() });
            onClose();
          } catch {}
        }}
        initialValues={{}}
      >
        <U21Spacer spacing={2}>
          <U21Spacer horizontal>
            <U21Typography variant="body2">
              This will restore the model-computed score of
            </U21Typography>
            <U21Chip
              variant="ghost"
              color={riskStyles.colorSchema}
              icon={<IconAlertTriangle />}
            >
              {riskStyles.label}: {Math.round(modelScore)}
            </U21Chip>
          </U21Spacer>
          <U21FormField
            label="Reason"
            name={'comment' satisfies keyof ClearOverrideFormValues}
            type={U21FormFieldTypes.TEXTAREA}
            required
            fieldProps={{
              placeholder:
                'Describe the reason for clearing this override (required for audit trail).',
            }}
            validate={(value?: string) =>
              !value?.trim() ? 'Reason is required.' : undefined
            }
          />
        </U21Spacer>
      </U21Form>
    </U21Modal>
  );
};
